import mediator from '../utilities/mediator';
import log from '../utilities/log';

import { ApiErrorResponse, Subject } from '../models';
import {
  mutationOptions,
  queryOptions,
  UseMutationOptions,
  UseQueryOptions,
} from '@tanstack/react-query';
import { EntityState, SubjectNoteAddRequest } from 'adc-sirius-module-common';

type SubjectsQueryParams = {
  state?: EntityState;
  siteKey?: string;
};

type SubjectQueryParams = {
  key?: string;
};

type CreateSubjectVariables = {
  subject: Subject;
};

type UpdateSubjectVariables = {
  subjectKey: Subject['key'];
  updatedSubjectValues: Partial<Subject>;
};

type CreateSubjectNoteVariables = {
  subjectKey: string;
  noteAddRequest: SubjectNoteAddRequest;
};

export function subjectsQueryOptions(
  params: SubjectsQueryParams,
  options?: Partial<UseQueryOptions<Subject[]>>,
) {
  return queryOptions<Subject[]>({
    ...options,
    queryKey: ['subjects', params],
    queryFn: async () => {
      try {
        return await mediator.request<Subject[]>('subject:getSubjects', {
          ...params,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled: !!params.siteKey,
  });
}

export function subjectQueryOptions(
  params: SubjectQueryParams,
  options?: Partial<UseQueryOptions<Subject>>,
) {
  return queryOptions<Subject>({
    ...options,
    queryKey: ['subject', params],
    queryFn: async () => {
      try {
        return await mediator.request<Subject>('subject:getSubjectByKey', {
          ...params,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled: !!params.key,
  });
}

export function createSubjectMutateOptions(
  options?: Partial<UseMutationOptions<Subject, Error, CreateSubjectVariables>>,
) {
  return mutationOptions<Subject, Error, CreateSubjectVariables>({
    ...options,
    mutationFn: async ({ subject }: CreateSubjectVariables) => {
      try {
        return await mediator.request<Subject>('subject:createSubject', {
          data: subject,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
  });
}

export function updateSubjectMutateOptions(
  options?: Partial<UseMutationOptions<Subject, Error, UpdateSubjectVariables>>,
) {
  return mutationOptions<Subject, Error, UpdateSubjectVariables>({
    ...options,
    mutationFn: async ({ updatedSubjectValues }: UpdateSubjectVariables) => {
      try {
        return await mediator.request<Subject>('subject:updateSubject', {
          data: updatedSubjectValues,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
  });
}

export function createSubjectNoteMutateOptions(
  options?: Partial<
    UseMutationOptions<Subject, Error, CreateSubjectNoteVariables>
  >,
) {
  return mutationOptions<Subject, Error, CreateSubjectNoteVariables>({
    ...options,
    mutationFn: async ({
      subjectKey,
      noteAddRequest,
    }: CreateSubjectNoteVariables) => {
      try {
        return await mediator.request<Subject>('subject:createSubjectNote', {
          key: subjectKey,
          data: noteAddRequest,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
  });
}
